import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { formatDate } from 'lib/helpers/dateUtils';
import { hasModule, iaRa, inRn, mString, pm } from 'lib/helpers/utility';
import { REFUNDED_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import { DOC_GAPS } from 'components/shared/DocumentSheet/lib/docStyles';
import DocTable from 'components/shared/DocumentSheet/components/DocTable';
import { getSaleAmount } from 'components/shared/Receipt/lib/receiptAmounts';
import DocNoteBox from 'components/shared/DocumentSheet/components/DocNoteBox';
import { modulesState, permissions as permissionsState } from 'components/state';
import DocSectionHead from 'components/shared/DocumentSheet/components/DocSectionHead';

import { ITEMS_TABLE_COLUMNS } from './columnDefs';
import { buildTransactionItems, hasTransactionItems } from './buildTransactionItems';

const REFUND_LABELS = {
  refunded: 'Refunded',
  partially_refunded: 'Partially refunded',
  voided: 'Voided'
};

const cellValue = ({ column, row }) => {
  switch (column.key) {
    case 'name':
      return row.name || 'Sale';
    case 'code':
      return row.procedure_code || '';
    case 'quantity':
      return String(inRn(row.quantity) || 1);
    default:
      return mString(inRn(row[column.key]));
  }
};

export const useReceiptItemsBlock = ({ transactions, isPatient, timezone, provider, payer }) => {
  const userPermissions = useRecoilValue(permissionsState);
  const practiceModules = useRecoilValue(modulesState);

  const canSeeAmounts =
    isPatient ||
    (pm(userPermissions, 'payments.read') && hasModule(practiceModules, 'medipay.payment_suite'));
  const hasTaxModule = hasModule(practiceModules, 'medipay.tax');

  const list = iaRa(transactions).filter(Boolean);
  const hasItems = list.some(hasTransactionItems);

  const columns = useMemo(
    () => ITEMS_TABLE_COLUMNS({ hasItems, canSeeAmounts, hasTaxModule }),
    [hasItems, canSeeAmounts, hasTaxModule]
  );

  const groups = useMemo(
    () =>
      list.map((transaction, index) => {
        const items = buildTransactionItems(transaction);
        const status = transaction?.status?.toLowerCase();

        const rows = items.length
          ? items
          : [
              {
                name: 'Sale',
                total: getSaleAmount(transaction) || inRn(transaction?.amount_total),
                refundLabel: REFUNDED_TRANSACTION_STATUSES.includes(status)
                  ? REFUND_LABELS[status]
                  : ''
              }
            ];

        return {
          key: transaction?.id ?? `transaction-${index}`,
          heading: transaction?.date_of_service ? 'Date of service' : '',
          date: formatDate(transaction?.date_of_service, timezone, 'MM/dd/yyyy'),
          rows: rows.map((row, rowIndex) => ({
            key: `${transaction?.id ?? index}-${row?.id ?? rowIndex}`,
            titleSuffix: items.length ? row.itemType : row.refundLabel,
            cells: columns.map((column) => ({
              value: cellValue({ column, row }),
              align: column.align
            }))
          }))
        };
      }),
    [list, columns, timezone]
  );

  if (!groups.length || !columns.length) return { canSeeAmounts, block: null };

  return {
    canSeeAmounts,
    block: {
      key: 'items',
      leading: (
        <div className={DOC_GAPS.section}>
          <DocSectionHead title="Items this payment covers" />
          {canSeeAmounts && !payer && (
            <DocNoteBox className="mb-2">
              Self-pay receipt – amounts shown are what{' '}
              <span className="font-700 text-neutral-900">you paid</span>; this is not an insurance
              statement.
              {provider?.name && (
                <>
                  {' Rendering provider: '}
                  <span className="font-700 text-neutral-900">{provider.name}</span>
                  {provider?.npi ? ` · NPI ${provider.npi}` : ''}
                </>
              )}
            </DocNoteBox>
          )}
        </div>
      ),
      table: { columns, groups },
      render: (props) => <DocTable {...props} />
    }
  };
};
